import dayjs from "dayjs"
import { cleanTimezone } from "./time-utils"
import { Party, ReorderPartyApiParams, ReorderPartyApiQuery } from "@/api/interface/party"
import { updateParty, reorderParties } from "@/api/nextme/parties"
import { Queue } from "@/api/interface/queue"
import { CustomField } from "@/api/interface/custom-field"
import { Media } from "@/api/interface/media"
import { updateQueueOrder } from '@/api/nextme/queues'
import { VwrMedia } from '@/api/interface/vwr'

const ORDER_COLUMNS = {
  party: 'orderColumn',
  queue: 'orderColumn',
  cf: 'orderColumn',
  media: 'orderColumn',
}

/**
 * For parties
 */

export const setMovedPartyOrderColumn = (
  movedItemId: string,
  items: Array<Partial<Party>>
): Partial<Party> => {
  // if no items
  if (!items?.length || items.length === 0) return

  const orderColumn = ORDER_COLUMNS['party']
  const movedItem = items?.find((item) => item.id === movedItemId)
  const movedIndex = items?.findIndex((item) => item.id === movedItemId)

  // if moved to start of list
  if (movedIndex <= 0) {
    const updatedOrderColumn = dayjs(
      cleanTimezone(items[movedIndex + 1].orderColumn)
    )
      .subtract(1, 'second')
      .format('YYYY-MM-DD hh:mm:ss')
    return {
      ...movedItem,
      [orderColumn]: updatedOrderColumn,
    }
  }

  // if moved to end of list
  if (movedIndex >= items.length - 1) {
    const $updatedOrderColumn = dayjs(
      cleanTimezone(items[movedIndex - 1].orderColumn)
    ).add(1, 'second')
    const updatedOrderColumn = dayjs(
      cleanTimezone(items[movedIndex - 1].orderColumn)
    )
      .add(1, 'second')
      .format(`YYYY-MM-DD HH:mm:ss.${$updatedOrderColumn.get('milliseconds')}`)
    return {
      ...movedItem,
      [orderColumn]: updatedOrderColumn,
    }
  }

  // if moved in middle of list
  if (movedIndex !== 0 || movedIndex !== items.length - 1) {
    const prevOrderColumn = dayjs(
      cleanTimezone(items?.[movedIndex - 1][orderColumn])
    )
    const nextOrderColumn = dayjs(
      cleanTimezone(items?.[movedIndex + 1][orderColumn])
    )
    const diff = parseFloat(
      nextOrderColumn.diff(prevOrderColumn, 'milliseconds', true).toPrecision(5)
    )
    const $updatedOrderColumn = prevOrderColumn.add(diff / 2)

    const updatedOrderColumn = $updatedOrderColumn.format(
      `YYYY-MM-DDTHH:mm:ss.${$updatedOrderColumn.get('milliseconds')}`
    )
    return {
      ...movedItem,
      [orderColumn]: updatedOrderColumn,
    }
  }

  return movedItem
}

// Not used unless we do DnD party reordering
export const buildReorderPartiesQuery = (
  movedItemId: string,
  originalItems: Array<Partial<Party>>,
  reorderedItems: Array<Partial<Party>>
) => {
  const newIndex = reorderedItems.findIndex((item) => item.id === movedItemId)
  const replacedItemId = originalItems[newIndex]?.id
  const direction: ReorderPartyApiQuery['direction'] =
    newIndex < originalItems.length - 1 ? 'before' : 'after'

  return [
    {
      movingParty: movedItemId,
      staticParty: replacedItemId,
      direction,
    },
  ]
}

export const updatePartyOrder = async ({
  movedItemId,
  originalItems,
  reorderedItems,
  businessId,
  locationId,
  queueId,
}: {
  movedItemId: string
  originalItems: Array<Partial<Party>>
  reorderedItems: Array<Partial<Party>>
  businessId: string
  locationId: string
  queueId?: string
}) => {
  const changes = buildReorderPartiesQuery(
    movedItemId,
    originalItems,
    reorderedItems
  )
  const params = {
    changes,
    businessId,
    locationId,
    queueId,
  }
  try {
    await reorderParties(params)
  } catch (error) {
    console.error(error)
  }
}

// Set new party order column directly from position without drag and drop
export const setPartyOrderColumnFromPosition = (
  position: number,
  movedItem: Partial<Party>,
  items: Array<Partial<Party>>
) => {
  // no items
  if (!items?.length || items.length === 1) return

  // first position
  if (position === 1) {
    const newOrderColumn = dayjs(cleanTimezone(items[0].orderColumn))
      .subtract(1, 'second')
      .format('YYYY-MM-DD HH:mm:ss')
    return {
      ...movedItem,
      orderColumn: newOrderColumn,
    }
  }

  // last position
  if (position === items[items.length - 1].position) {
    const $updatedOrderColumn = dayjs(
      cleanTimezone(items[items.length - 1].orderColumn)
    ).add(1, 'second')
    const updatedOrderColumn = $updatedOrderColumn.format(
      `YYYY-MM-DD HH:mm:ss.${$updatedOrderColumn.get('milliseconds')}`
    )
    return {
      ...movedItem,
      orderColumn: updatedOrderColumn,
    }
  }

  // middle position
  if (position !== 1 && position !== items[items.length - 1].position) {
    const nextItem = items?.find((item) => item.position === position)
    const prevItem = items?.find((item) => item.position === position - 1)
    const $updatedOrderColumn = dayjs(cleanTimezone(prevItem.orderColumn)).add(
      dayjs(cleanTimezone(nextItem.orderColumn)).diff(
        dayjs(cleanTimezone(prevItem.orderColumn)),
        'second',
        true
      )
    )

    const updatedOrderColumn = $updatedOrderColumn.format(
      `YYYY-MM-DD HH:mm:ss.${$updatedOrderColumn.get('milliseconds')}`
    )
    return {
      ...movedItem,
      orderColumn: updatedOrderColumn,
    }
  }

  return movedItem
}

export const updateLinePosition = async (
  position: number,
  movedItem: Partial<Party>,
  items: Array<Partial<Party>>
) => {
  try {
    const partyToUpdate = setPartyOrderColumnFromPosition(
      position,
      movedItem,
      items
    )
    await updateParty(partyToUpdate)
  } catch (error) {
    console.error(error)
  }
}

/**
 * Settings objects: Queues, Custom Fields and VWR Images
 */

type ReorderedItems = Array<Partial<Queue | CustomField | Media>>

export const getMovedItemPosition = (
  movedItemId: any,
  items: ReorderedItems
) => {
  const movedIndex = items?.findIndex((item) => item.id === movedItemId)
  if (movedIndex >= 0) {
    return movedIndex + 1
  }
  return undefined
}

// For VWR media, we can't use reposition, and must use the reorder endpoint
// which requires movedItemId, staticItemId and direction
export const buildReorderMediaQuery = (
  movedItemId: any,
  items: Media[]
) => {
  let direction = 'before'
  let staticItem
  const movedIndex = items?.findIndex((item) => item.id === movedItemId)

  if (movedIndex === items.length - 1) {
    direction = 'after'
    staticItem = items[movedIndex - 1]
  } else {
    staticItem = items[movedIndex + 1]
  }
  
  return  {
    movingMedia: movedItemId,
    staticMedia: staticItem?.id,
    direction,
  }
}